import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { FaSpinner, FaCheckCircle, FaTimesCircle, FaExternalLinkAlt } from 'react-icons/fa';

const MappingNotificationContext = createContext();

export const useMappingNotification = () => useContext(MappingNotificationContext);

export const MappingNotificationProvider = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [result, setResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Check for mapping result on mount and periodically
  useEffect(() => {
    const checkForResult = () => {
      // Check if mapping is in progress
      const pending = sessionStorage.getItem('pendingMapping');
      setIsProcessing(!!pending);
      
      // Check for completed result - show toast on any page
      const storedResult = sessionStorage.getItem('mappingResult');
      if (storedResult) {
        try {
          const parsed = JSON.parse(storedResult);
          console.log('[MappingNotification] Found result:', parsed);
          sessionStorage.removeItem('mappingResult');
          sessionStorage.removeItem('pendingMapping');
          setResult(parsed);
          setShowToast(true); // Show toast first, not modal
          setIsProcessing(false);
        } catch (e) {
          console.error('[MappingNotification] Failed to parse mapping result:', e);
          sessionStorage.removeItem('mappingResult');
        }
      }
    };

    // Check immediately on mount
    checkForResult();
    
    // Poll every 500ms for faster response
    const interval = setInterval(checkForResult, 500);
    return () => clearInterval(interval);
  }, []);

  // Auto-hide toast after 30 seconds and clear processing state
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setResult(null);
        setIsProcessing(false);
        // Also clear sessionStorage to prevent showing again
        sessionStorage.removeItem('pendingMapping');
        sessionStorage.removeItem('mappingResult');
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const openModal = useCallback(() => {
    setShowToast(false);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setResult(null);
    setIsProcessing(false);
    // Clear sessionStorage when modal is closed
    sessionStorage.removeItem('pendingMapping');
    sessionStorage.removeItem('mappingResult');
  }, []);

  const closeToast = useCallback(() => {
    setShowToast(false);
    setResult(null);
    setIsProcessing(false);
    // Clear sessionStorage when toast is closed
    sessionStorage.removeItem('pendingMapping');
    sessionStorage.removeItem('mappingResult');
  }, []);

  const goToOrderManagement = useCallback(() => {
    setShowModal(false);
    setIsProcessing(false);
    sessionStorage.removeItem('pendingMapping');
    window.open('/admin/order-management', '_blank');
  }, []);

  return (
    <MappingNotificationContext.Provider value={{ showModal, result, isProcessing, closeModal }}>
      {children}
      
      {/* Processing Indicator - Fixed Bottom Right */}
      {isProcessing && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#1e293b',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 99999,
          fontSize: '13px',
          fontWeight: 500
        }}>
          <FaSpinner style={{ animation: 'spin 1s linear infinite', width: '16px', height: '16px' }} />
          <span>Mapping ASIN in background...</span>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      
      {/* Toast Notification - Fixed Bottom Right */}
      {showToast && result && (
        <div 
          onClick={openModal}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: result.success ? '#166534' : '#991b1b',
            color: '#fff',
            padding: '14px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            zIndex: 99999,
            cursor: 'pointer',
            maxWidth: '400px',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          {result.success ? (
            <FaCheckCircle style={{ fontSize: '20px', flexShrink: 0 }} />
          ) : (
            <FaTimesCircle style={{ fontSize: '20px', flexShrink: 0 }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              {result.success ? 'ASIN Mapped Successfully!' : 'Mapping Failed'}
            </div>
            {result.success && result.convertedOrders > 0 && (
              <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px' }}>
                {result.convertedOrders} order(s) converted
              </div>
            )}
            <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px' }}>
              Click to view details
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); closeToast(); }}
            style={{ 
              background: 'rgba(255,255,255,0.2)', 
              border: 'none', 
              color: '#fff', 
              width: '24px', 
              height: '24px', 
              borderRadius: '50%', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              flexShrink: 0
            }}
          >×</button>
          <style>{`@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        </div>
      )}
      
      {/* Result Modal - Opens when toast is clicked */}
      {showModal && result && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '10px',
            padding: '20px',
            maxWidth: '480px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              {result.success ? (
                <FaCheckCircle style={{ color: '#16a34a', fontSize: '24px' }} />
              ) : (
                <FaTimesCircle style={{ color: '#dc2626', fontSize: '24px' }} />
              )}
              <h3 style={{ 
                margin: 0, 
                fontSize: '16px', 
                fontWeight: 600,
                color: result.success ? '#16a34a' : '#dc2626',
                flex: 1
              }}>
                {result.success ? 'ASIN Mapping Successful' : 'Mapping Failed'}
              </h3>
              <button 
                onClick={closeModal}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '0 4px', lineHeight: 1 }}
              >×</button>
            </div>
            
            {result.success ? (
              <>
                {/* Mapping Details */}
                <div style={{ 
                  backgroundColor: '#f0fdf4', 
                  padding: '12px', 
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '13px'
                }}>
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: '#64748b' }}>ASIN:</span> <strong style={{ color: '#166534' }}>{result.asin}</strong>
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: '#64748b' }}>Mapped SKU:</span> <strong style={{ color: '#166534' }}>{result.skuId}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Offer ID:</span> <strong style={{ color: '#166534' }}>{result.offerId}</strong>
                  </div>
                </div>
                
                {/* Converted Orders */}
                {result.convertedOrders > 0 ? (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: 600, 
                      color: '#1a1a1a',
                      marginBottom: '8px'
                    }}>
                      📦 {result.convertedOrders} Order(s) Converted
                    </div>
                    
                    {result.convertedOrderDetails?.length > 0 && (
                      <div style={{ 
                        maxHeight: '150px', 
                        overflowY: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.8fr', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          <div style={{ padding: '8px', fontWeight: 600, color: '#64748b' }}>Order ID</div>
                          <div style={{ padding: '8px', fontWeight: 600, color: '#64748b' }}>Importerr ID</div>
                          <div style={{ padding: '8px', fontWeight: 600, color: '#64748b', textAlign: 'right' }}>Amount</div>
                        </div>
                        {result.convertedOrderDetails.map((order, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.8fr', borderBottom: '1px solid #f3f4f6' }}>
                            <div style={{ padding: '6px 8px', color: '#374151', fontFamily: 'monospace', fontSize: '10px' }}>{order.orderId?.slice(-12)}</div>
                            <div style={{ padding: '6px 8px', color: '#3b82f6', fontWeight: 500 }}>{order.upsaleoOrderId || '-'}</div>
                            <div style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>₹{order.consumerPrice?.toLocaleString('en-IN') || 0}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px 10px', 
                      backgroundColor: '#fef3c7', 
                      borderRadius: '6px',
                      fontSize: '11px',
                      color: '#92400e'
                    }}>
                      Orders moved to Order Management with status "Payment Pending"
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#f3f4f6', 
                    borderRadius: '6px',
                    marginBottom: '12px',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    No failed orders found for this ASIN to convert.
                  </div>
                )}
                
                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  {result.convertedOrders > 0 && (
                    <button
                      onClick={goToOrderManagement}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      View Orders <FaExternalLinkAlt size={10} />
                    </button>
                  )}
                  <button
                    onClick={closeModal}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '5px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Error Message */}
                <div style={{ 
                  backgroundColor: '#fef2f2', 
                  padding: '12px', 
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '13px',
                  color: '#991b1b'
                }}>
                  {result.message || 'An error occurred while mapping the ASIN'}
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={closeModal}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '5px',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </MappingNotificationContext.Provider>
  );
};

export default MappingNotificationProvider;
